export type RowType = "aircraft" | "instructor";

export interface User {
  id: string;
  username: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
  instructor: Instructor;
  instructorId: string;
  student: Student;
  studentId: string;
  role: "ADMIN" | "INSTRUCTOR" | "USER";
}

export interface Reservation {
  id: string;
  rowType: RowType;
  rowId: string;
  startMin: number;
  endMin: number;
  // ✅ booléen d'AFFICHAGE pour CETTE ligne
  secondPosition: boolean;
  isSolo: boolean;
  groupId?: string;

  createdAt?: Date;
  createdBy?: User; // user qui a créé la résa
  updatedAt?: Date;
  instructor?: Instructor; // user lié à l’instructeur
  student?: Student; // user lié à l’élève
}

export type AvailabilityPolicy =
  | "AVAILABLE_BY_DEFAULT"
  | "UNAVAILABLE_BY_DEFAULT";

export type AvailabilityKind = "AVAILABLE" | "UNAVAILABLE";
export interface InstructorAvailability {
  kind: AvailabilityKind;
  start: string;
  end: string;
}

export interface Instructor {
  id: string;
  name: string;
  user: User;
  availabilityPolicy: string;
  availabilities: InstructorAvailability[];
}

export interface Student {
  id: string;
  name: string;
  user: User;
}

// DTOs utilisés par le service
export type CreateReservationDto = {
  aircraftId: string | null;
  instructorId: string | null;
  studentId: string | null;
  userId?: string | null;
  notes?: string;
  start: string;
  end: string;
  isSolo?: boolean;
  status?: "PENDING" | "CONFIRMED" | "CANCELLED";
  secondPositionAircraft?: boolean;
  secondPositionInstructor?: boolean;
};

export type UpdateReservationDto = Partial<CreateReservationDto>;

// Payload expected by POST /flights (CreateSchema)
export interface Flight {
  date: string | Date;
  aircraftId: string;
  pilotId: string;
  instructorId?: string | null;
  examinerId?: string | null;
  pilotInCommand: boolean;
  recurrenceTraining: boolean;
  proficiencyCheck: boolean;
  priceCategory: FlightPriceCategory;
  remarks?: string;
  origin: string;
  destination: string;
  counterBeginMinutes: number;
  counterEndMinutes: number;
  blockOff: string | Date;
  takeoff: string | Date;
  landing: string | Date;
  blockOn: string | Date;
  instrumentTime: number;
  nightTime: number;
  oilRefilled?: string; // Prisma.Decimal-compatible
  fuelRefilled?: string; // Prisma.Decimal-compatible
  passengers: number;
  moua: number;
  landings: LandingItem[];

  // Retour de l'API
  id?: string;
  aircraft?: Aircraft;
  pilot?: User;
  instructor?: User;
}

// Landing item model
export interface LandingItem {
  approachType: LandingApproachType;
  isNight: boolean;
  count: number;
}

export type FlightPriceCategory =
  | "NONE"
  | "BASIC"
  | "SOLO_SUP"
  | "NIGHT"
  | "FI_I"
  | "IR";
export type LandingApproachType = "VISUAL" | "PA_3D" | "NPA_2D";

// Réponse API (backend)
export interface ApiReservation {
  id: string;
  aircraftId: string | null;
  instructorId: string | null;
  studentId: string | null;
  userId?: string | null;
  start: string; // ISO
  end: string; // ISO
  isSolo: boolean;
  status: "PENDING" | "CONFIRMED" | "CANCELLED";
  notes?: string;

  // ✅ colonnes par ressource
  secondPositionAircraft?: boolean;
  secondPositionInstructor?: boolean;
}

/* Optionnel, si tu tapes le résultat du dialog */
export type DialogResult = {
  action: "save" | "cancel";
  rowType: RowType;
  rowId: string;
  startMin: number;
  endMin: number;
  aircraftId?: string | null;
  instructorId?: string | null;
  notes?: string;
  startDate?: Date;
  endDate?: Date;
};

export interface Aircraft {
  id: string;
  code: string;
  model: string;
  condition: string;
  location: string;
  overhaulType: number;
  hoursAtMaintenance: number;
  counterMinutes: number | null;

  // API Response
  currentCounterMinutes?: number | null;
  nextMaintenance?: number;
}

// utils de mapping API ⇄ UI
export function mapApiToUiReservations(list: ApiReservation[]): Reservation[] {
  const result: Reservation[] = [];
  (list as any[]).forEach((r) => {
    const startMin = isoToMin(r.start);
    const endMin = isoToMin(r.end);

    const baseMeta = {
      createdBy: r?.createdBy,
      createdAt: r?.createdAt,
      updatedAt: r?.updatedAt,
      instructor: r?.instructor,
      student: r?.student,
    };

    if (r.aircraftId) {
      result.push({
        id: r.id,
        rowType: "aircraft",
        rowId: r.aircraftId,
        startMin,
        endMin,
        isSolo: !!r.isSolo,
        secondPosition: !!r.secondPositionAircraft,
        ...baseMeta,
      });
    }

    if (r.instructorId) {
      result.push({
        id: r.id,
        rowType: "instructor",
        rowId: r.instructorId,
        startMin,
        endMin,
        isSolo: !!r.isSolo,
        secondPosition: !!r.secondPositionInstructor,
        ...baseMeta,
      });
    }
  });
  return result;
}

function isoToMin(iso: string): number {
  const dt = new Date(iso);
  return dt.getHours() * 60 + dt.getMinutes();
}
